import React, { useState } from 'react';
import { AgentHistoryItem } from '../../types/user';
import { 
  X, 
  Calendar, 
  FileText, 
  Image as ImageIcon, 
  Type, 
  Sparkles,
  Clock,
  Share2,
} from 'lucide-react';
import ShareModal from '../common/ShareModal';

interface AgentHistoryModalProps {
  item: AgentHistoryItem | null;
  isOpen: boolean;
  onClose: () => void;
}

const AgentHistoryModal = ({ item, isOpen, onClose }: AgentHistoryModalProps) => {
  const [showShareModal, setShowShareModal] = useState(false);
  
  if (!isOpen || !item) return null;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAgentKindLabel = (kind: string) => {
    switch (kind) {
      case 'text-modal':
        return '텍스트 기반';
      case 'image-modal':
        return '이미지 기반';
      case 'gen-image-modal':
        return '이미지 생성';
      default:
        return kind;
    }
  };

  const getAgentKindIcon = (kind: string) => {
    switch (kind) {
      case 'text-modal':
        return <Type className="w-5 h-5" />;
      case 'image-modal':
        return <ImageIcon className="w-5 h-5" />;
      case 'gen-image-modal':
        return <Sparkles className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  const renderInputValue = (key: string, value: any) => {
    if (Array.isArray(value)) {
      return (
        <div className="flex flex-wrap gap-2">
          {value.map((item, index) => (
            <span key={index} className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
              {item}
            </span>
          ))}
        </div>
      );
    }
    
    // 이미지 객체인 경우 처리
    if (typeof value === 'object' && value !== null) {
      // 이미지 데이터가 있는 경우
      if (value.data && value.ext) {
        return (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <ImageIcon className="w-4 h-4" />
              <span>업로드된 이미지 ({value.ext.toUpperCase()})</span>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <img
                src={`data:image/${value.ext};base64,${value.data}`}
                alt="업로드된 이미지"
                className="max-w-xs max-h-48 rounded-lg shadow-sm object-contain"
              />
            </div>
          </div>
        );
      }
      
      // 다른 객체인 경우 JSON으로 표시
      return (
        <div className="bg-gray-50 rounded-lg p-3">
          <pre className="text-sm text-gray-700 whitespace-pre-wrap">
            {JSON.stringify(value, null, 2)}
          </pre>
        </div>
      );
    }
    
    if (typeof value === 'string') {
      return <span className="text-gray-900">{value}</span>;
    }
    
    return <span className="text-gray-900">{String(value)}</span>;
  };

  const getQuestionFromSchema = (key: string) => {
    const property = item.agent_schema.properties[key];
    return property?.description || key;
  };

  const handleShare = () => {
    if (!item.id) return;
    setShowShareModal(true);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      <div className="flex min-h-full items-center justify-center p-4 font-serif-kr">
        <div className="relative bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-4">
              <img
                src={item.agent_image}
                alt={item.agent_title}
                className="w-10 h-10 md:w-12 md:h-12 rounded-lg object-cover"
              />
              <div className="flex-1 min-w-0">
                <h2 className="text-lg md:text-xl font-bold text-gray-900 tracking-tight overflow-hidden text-ellipsis whitespace-nowrap">{item.agent_title}</h2>
                <div className="flex items-center gap-2 mt-1 text-sm md:text-base">
                  {getAgentKindIcon(item.agent_kind)}
                  <span className="text-xs md:text-sm text-gray-600">{getAgentKindLabel(item.agent_kind)}</span>
                  {item.child_license && (
                    <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">
                      아동용
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {item.id && (
                <button
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 px-2 md:px-3 py-2 text-[10px] md:text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 shadow-sm whitespace-pre-line md:whitespace-nowrap"
                >
                  <Share2 className="w-4 h-4" />
                  {`공유하기`}
                </button>
              )}
            </div>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
            <div className="mb-8">
              <h3 className="text-base md:text-lg font-semibold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-teal-500" />
                기본 정보
              </h3>
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <p className="text-gray-700 text-sm md:text-base">{item.agent_description}</p>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>생성일: {formatDate(item.created_at)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-base md:text-lg font-semibold mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-teal-500" />
                입력한 답변
              </h3>
              <div className="space-y-4">
                {Object.entries(item.input_values).map(([key, value]) => (
                  <div key={key} className="bg-white border border-gray-200 rounded-xl p-4">
                    <h4 className="font-medium text-gray-900 mb-2 text-sm md:text-base">
                      {getQuestionFromSchema(key)}
                    </h4>
                    {renderInputValue(key, value)}
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-base md:text-lg font-semibold mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-teal-500" />
                결과
              </h3>
              
              {/* 이미지 결과 */}
              {item.binary_values && item.binary_values.startsWith('http') && (
                <div className="mb-6">
                  <h4 className="font-medium text-gray-900 mb-3 text-sm md:text-base">생성된 이미지</h4>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <img
                      src={item.binary_values}
                      alt="생성된 결과 이미지"
                      className="w-full max-w-md mx-auto rounded-lg shadow-md"
                    />
                  </div>
                </div>
              )}

              {/* 텍스트 결과 */}
              {item.output_values && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3 text-sm md:text-base">분석 결과</h4>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="prose prose-sm max-w-none">
                      {item.output_values.split('\n').map((line, index) => {
                        if (line.trim() === '') return <br key={index} />;
                        
                        if (line.startsWith('###')) {
                          return (
                            <h3 key={index} className="text-base md:text-lg font-semibold text-gray-900 mt-4 mb-2">
                              {line.replace('###', '').trim()}
                            </h3>
                          );
                        }
                        if (line.startsWith('**') && line.endsWith('**')) {
                          return (
                            <h4 key={index} className="font-semibold text-gray-800 mt-3 mb-1 text-sm md:text-base">
                              {line.replace(/\*\*/g, '')}
                            </h4>
                          );
                        }
                        
                        return (
                          <p key={index} className="text-gray-700 mb-2 leading-relaxed text-sm md:text-base">
                            {line}
                          </p>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 공유 모달 */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        shareUrl={`${window.location.origin}/agent/share/${item.id}`}
        title="AI 분석 결과 공유"
      />
    </div>
  );
};

export default AgentHistoryModal; 